import { AppData } from '@scts/app-data';
import { PageHeaderSg, WarningText } from '@scts-ui/component-library';
import { Appeal, BenefitType, Party } from '@scts/models';
import { caseRefSplit, getEnumKeyByEnumValue, useTitleService, longDateFormatter } from '@scts/utilities';
import { useState } from 'react';
import { Table } from 'react-bootstrap';

export interface AppealsMemberProps {
  memberAppeals: Appeal[];
}

const AppealsMember = (appealsMemberProps: AppealsMemberProps) => {
  useTitleService('Case List: Social Security Chamber');
  const [filter, setFilter] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ sortKey: string | null; direction: 'asc' | 'desc' }>({ 
    sortKey: null, 
    direction: 'asc' 
  });


  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Add sorting handler
  const handleSort = (sortKey : string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.sortKey === sortKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ sortKey, direction });
  };

  // Add sort icon function
  const getSortIcon = (columnKey : string) => {
    if (sortConfig.sortKey !== columnKey) {
      return <i className="bi bi-arrow-down-up ms-1"></i>;
    }
    return sortConfig.direction === 'asc'
      ? <i className="bi bi-arrow-up ms-1"></i>
      : <i className="bi bi-arrow-down ms-1"></i>;
  };


  const workOutNameList = (partyList: Party[]) => {
    let nameList = '';
    partyList.forEach((party) => {
      if (party.type === 'Appellant') {
        nameList += party.firstName + ' ' + party.lastName + ', ';
      }
    });

    return nameList.substring(0, nameList.lastIndexOf(','));
  };

  const doSomeFiltering = (f: Appeal) => {
    return (
      getEnumKeyByEnumValue(BenefitType, f.benefit).toLowerCase().includes(filter.toLowerCase()) ||
      f.status.toLowerCase().includes(filter.toLowerCase()) ||
      f.caseReference.toLowerCase().includes(filter.toLowerCase()) ||
      workOutNameList(f.parties).toLowerCase().includes(filter.toLowerCase()) ||
      (f.hearing?.date && longDateFormatter(new Date(f.hearing.date)).toLowerCase().includes(filter.toLowerCase())) ||
      filter === ''
    );
  };

  const getFilteredRows = () => {
    try {
      let filteredAppeals = appealsMemberProps.memberAppeals
        .filter((f) => doSomeFiltering(f));
      //Apply sorting if hearing date is selected, or default to date sorting
      if (sortConfig.sortKey === 'hearingDate' || sortConfig.sortKey === null) {
        filteredAppeals.sort((a, b) => {
          const aValue = a.hearing?.date ? new Date(a.hearing.date) : new Date(0);
          const bValue = b.hearing?.date ? new Date(b.hearing.date) : new Date(0);

          const direction = sortConfig.sortKey ? sortConfig.direction : 'asc'; // Default ascending
          if (aValue < bValue) return direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return direction === 'asc' ? 1 : -1;
          return 0;
        });
      }

      //Temp change depending on whether business need sorting on all columns or not.
      // Apply sorting if a sort key is selected
      // if (sortConfig.sortKey) {
      //   filteredAppeals.sort((a, b) => {
      //     let aValue, bValue;

      //     switch (sortConfig.sortKey) {
      //       case 'caseReference':
      //         aValue = a.caseReference?.toLowerCase() || '';
      //         bValue = b.caseReference?.toLowerCase() || '';
      //         break;
      //       case 'hearingDate':
      //         aValue = a.hearing?.date ? new Date(a.hearing.date) : new Date(0);
      //         bValue = b.hearing?.date ? new Date(b.hearing.date) : new Date(0);
      //         break;
      //       case 'status':
      //         aValue = a.status?.toLowerCase() || '';
      //         bValue = b.status?.toLowerCase() || '';
      //         break;
      //       case 'benefit':
      //         aValue = getEnumKeyByEnumValue(BenefitType, a.benefit)?.toLowerCase() || '';
      //         bValue = getEnumKeyByEnumValue(BenefitType, b.benefit)?.toLowerCase() || '';
      //         break;
      //       case 'appellantNames':
      //         aValue = workOutNameList(a.parties)?.toLowerCase() || '';
      //         bValue = workOutNameList(b.parties)?.toLowerCase() || '';
      //         break;
      //       default:
      //         return 0;
      //     }

      //     if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      //     if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      //     return 0;
      //   });
      // }
      return filteredAppeals
        .map((appeal, index) => (
          <tr key={index} className="detail-row">
            <td className="break-caseref">
              {<a href={'/appeal/' + encodeURIComponent(appeal.caseReference)}>
                <span className="link-text">
                  {caseRefSplit(appeal.caseReference)}
                </span>
              </a>}
            </td>
            <td>{appeal.hearing?.date ? longDateFormatter(new Date(appeal.hearing?.date)) : '-'}</td>
            <td>{appeal.status}</td>
            <td>{getEnumKeyByEnumValue(BenefitType, appeal.benefit)} </td>
            <td> {workOutNameList(appeal.parties)}</td>
          </tr>
        ));
    } catch (error) {
      console.log('error:', error);
      return [];
    }
  };

  return (
    <div className=" member-appeals-wrapper">
      <PageHeaderSg headerText={'Member Appeal List'} />
      <div className="card">
        <div className="card-body">
          {appealsMemberProps.memberAppeals.length > 1 && (
            <div className="d-flex flex-column member-filter-box card-header">
              <div className="d-flex flex-row filter-header" onClick={toggleCollapse} style={{ cursor: 'pointer' }}>
                <i className="bi bi-filter-right pe-2 fs-2 filter-icon"></i>
                <p className="filter-text">Filter</p>
                <i className={`bi ${isCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'} ms-auto`}></i>
              </div>
              <div className={`collapse ${!isCollapsed ? 'show' : ''}`}>
                <div className="filter-text">
                  <pre>{AppData.filterTextAbridged}</pre>
                </div>

                <div className="d-flex flex-column flex-sm-row">
                  <p className="d-flex flex-row">
                    <input
                      id="filter"
                      name="filter"
                      placeholder="Search all appeals"
                      value={filter}
                      type="text"
                      onChange={(event) => setFilter(event.target.value)}
                      className="member-filter me-2"
                      aria-label="search-box"
                    />
                  </p>
                  <p className="pt-1 alert-success">
                    <span
                      className="member-filter-number fst-italic"
                      aria-live="polite"
                    >
                      {+getFilteredRows().length} appeals in filter
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}
          {+getFilteredRows().length === 0 && (
            <WarningText
              warningText={
                'There are zero results for your filter. Please check for a typo or that you may have accidently typed extra spaces.'
              }
            />
          )}
          <div className="table-members">
            <Table striped hover responsive size="sm">
              <thead>
                <tr>
                  <th scope="col">Case Ref</th>
                  <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('hearingDate')}>
                    Hearing date {getSortIcon('hearingDate')}
                  </th>
                  <th scope="col">Status</th>
                  <th scope="col">Benefit</th>
                  <th scope="col">Appellant Names</th>
                </tr>
              </thead>
              <tbody>{getFilteredRows()}</tbody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppealsMember;
